import { useState, useEffect } from 'react'
import { UserPlus, UserCheck, UserX, Users, Clock } from 'lucide-react'
import { documentPermissionsApi, staffApi } from '../lib/api'
import { useAuth } from '../hooks/useAuth'
import { useToast } from '../lib/ToastContext'
import { Badge, Button, Modal, Select, Spinner } from './ui'

export default function DocumentPermissionManager({ documentId, documentLabel }) {
  const [permissions, setPermissions] = useState([])
  const [staff, setStaff] = useState([])
  const [showModal, setShowModal] = useState(false)
  const [selectedStaff, setSelectedStaff] = useState('')
  const [permissionType, setPermissionType] = useState('view')
  const [loading, setLoading] = useState(true)
  const [granting, setGranting] = useState(false)
  const { user, company } = useAuth()
  const { toast } = useToast()

  useEffect(() => {
    if (!documentId) return
    loadData()
  }, [documentId])

  const loadData = async () => {
    setLoading(true)
    const [permsRes, staffRes] = await Promise.all([
      documentPermissionsApi.getDocumentPermissions(documentId),
      staffApi.list(company?.id),
    ])
    if (!permsRes.error) setPermissions(permsRes.data || [])
    if (!staffRes.error) setStaff(staffRes.data || [])
    setLoading(false)
  }

  const handleGrant = async () => {
    if (!selectedStaff || !permissionType) {
      toast('Please select a staff member and permission type.', 'error')
      return
    }
    
    setGranting(true)
    const { error } = await documentPermissionsApi.grantPermission(
      documentId,
      selectedStaff,
      permissionType,
      user?.id
    )
    
    if (!error) {
      toast(`Granted ${getPermissionLabel(permissionType)} permission to staff.`, 'success')
      setShowModal(false)
      setSelectedStaff('')
      loadData()
    } else {
      toast('Failed to grant permission: ' + error, 'error')
    }
    setGranting(false)
  }

  const handleRevoke = async (staffId, permissionType) => {
    if (!confirm('Revoke this permission?')) return
    const { error } = await documentPermissionsApi.revokePermission(
      documentId,
      staffId,
      permissionType
    )
    if (!error) {
      toast('Permission revoked.', 'info')
      loadData()
    }
  }

  const getPermissionLabel = (type) => {
    const labels = {
      view: 'Can View',
      edit: 'Can Edit',
      review: 'Can Review'
    }
    return labels[type] || type
  }

  const getPermissionColor = (type) => {
    const colors = {
      view: 'border-blue-300 text-blue-700 bg-blue-50',
      edit: 'border-amber-300 text-amber-700 bg-amber-50',
      review: 'border-green-300 text-green-700 bg-green-50'
    }
    return colors[type] || ''
  }

  if (loading) return <Spinner size="sm" />

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Users size={14} className="text-neutral-400" />
          <span className="text-xs font-medium text-ink">Access</span>
          {permissions.length > 0 && (
            <Badge variant="default" size="sm">{permissions.length}</Badge>
          )}
        </div>
        <Button 
          variant="ghost" 
          size="xs" 
          onClick={() => setShowModal(true)}
          className="text-xs"
        >
          <UserPlus size={12} className="mr-1" /> Grant
        </Button>
      </div>

      {permissions.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {permissions.map((perm) => (
            <Badge 
              key={perm.id} 
              variant="default" 
              size="sm"
              className={`flex items-center gap-1 ${getPermissionColor(perm.permission_type)}`}
            >
              {perm.profiles?.full_name?.split(' ')[0] || 'Staff'}
              <span className="text-[9px] opacity-60">({getPermissionLabel(perm.permission_type).split(' ')[1]})</span>
              <button
                onClick={() => handleRevoke(perm.staff_id, perm.permission_type)}
                className="ml-0.5 hover:text-error transition-colors"
                aria-label="Revoke permission"
              >
                <UserX size={10} />
              </button>
            </Badge>
          ))}
        </div>
      )}

      {/* Grant Permission Modal */}
      <Modal open={showModal} onClose={() => setShowModal(false)} title="Grant Document Access" size="sm">
        <div className="space-y-4">
          <p className="text-sm text-muted">
            Grant access to <span className="font-semibold text-ink">"{documentLabel}"</span>
          </p>
          
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-muted mb-1.5">
              Staff Member
            </label>
            <Select
              options={staff.map(s => ({ value: s.id, label: s.name || s.email }))}
              value={selectedStaff}
              onChange={setSelectedStaff}
              placeholder="Select a staff member..."
            />
          </div>

          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-muted mb-1.5">
              Permission Level
            </label>
            <div className="grid grid-cols-3 gap-2">
              {['view', 'review', 'edit'].map((type) => (
                <button
                  key={type}
                  onClick={() => setPermissionType(type)}
                  className={`px-3 py-2 text-xs font-semibold border rounded-lg transition-colors ${
                    permissionType === type
                      ? 'border-brand bg-brand-soft text-brand'
                      : 'border-neutral-200 text-muted hover:border-neutral-300'
                  }`}
                >
                  {getPermissionLabel(type)}
                </button>
              ))}
            </div>
            <p className="text-[10px] text-muted mt-1.5">
              {permissionType === 'view' && 'Can view document content (read-only)'}
              {permissionType === 'review' && 'Can view and add comments/review'}
              {permissionType === 'edit' && 'Can upload new versions'}
            </p>
          </div>

          <div className="flex gap-3 pt-2">
            <Button variant="secondary" onClick={() => setShowModal(false)} className="flex-1">
              Cancel
            </Button>
            <Button 
              variant="primary" 
              onClick={handleGrant} 
              className="flex-1"
              disabled={!selectedStaff || granting}
            >
              {granting ? <Spinner size="sm" /> : <><UserCheck size={14} className="mr-1" /> Grant</>}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  )
}